import { useState, type ReactNode } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  LayoutGrid,
  Loader2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { OpCallout } from "@/components/op/OpCallout";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";
import {
  StudioModalBody,
  StudioModalFooter,
  StudioModalHeader,
} from "@/components/StudioModalFrame";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OP_RESULT_CARD, OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

export type OpAiJobDemoState = "done" | "running" | "failed" | "empty";
type LayoutMode = "hierarchy" | "equal";

const PARAMS = [
  { label: "Model", value: "gemini-3.5-flash" },
  { label: "Typ", value: "mieszanka" },
  { label: "Notatka", value: "brak" },
  { label: "Ból", value: "Ciało / wygląd" },
  { label: "Styl", value: "Krem Kasi" },
];

const CARDS = [
  { tag: "signals", text: "Przed wizytą u matki przebierasz się trzy razy." },
  { tag: "vs-echo", text: "Może powinnaś trochę schudnąć, mówię to z troski." },
  { tag: "dialogue", text: "Naprawdę zjesz jeszcze dokładkę przy tej wadze?" },
  { tag: "swap", text: "Od razu się tłumaczysz, gdy matka patrzy na brzuch." },
];

const LOG_DONE = [
  { t: "15:47:24", kind: "worker_start", text: "concepts" },
  { t: "15:47:24", kind: "memory_ready", text: "prompt=7.9k zn" },
  { t: "15:47:24", kind: "prompt gotowy", text: "7.9k zn" },
  { t: "15:47:24", kind: "wysyłam", text: "gemini-3.5-flash" },
  { t: "15:47:39", kind: "czekam na znak", text: "15s" },
  { t: "15:47:40", kind: "leci tekst", text: "pierwszy znak" },
  { t: "15:48:25", kind: "odpowiedź", text: "7.4k zn · 61s" },
];

const LOG_RUNNING = [
  { t: "15:47:24", kind: "worker_start", text: "concepts" },
  { t: "15:47:24", kind: "memory_ready", text: "prompt=7.9k zn" },
  { t: "15:47:24", kind: "prompt gotowy", text: "7.9k zn" },
  { t: "15:47:24", kind: "wysyłam", text: "gemini-3.5-flash" },
  { t: "15:47:39", kind: "czekam na znak", text: "15s" },
  { t: "15:47:40", kind: "leci tekst", text: "2.1k zn…" },
];

function statusMeta(state: OpAiJobDemoState) {
  if (state === "running") {
    return {
      label: "Przetwarzam… odebrałem już ~2.1k znaków.",
      icon: <Loader2 className="h-5 w-5 animate-spin text-brand" />,
    };
  }
  if (state === "failed") {
    return {
      label: "Błąd modelu. Sprawdź log albo odpal ponownie.",
      icon: <XCircle className="h-5 w-5 text-destructive" />,
    };
  }
  if (state === "empty") {
    return {
      label: "Jeszcze nic. Status pojawi się po starcie joba.",
      icon: <Loader2 className="h-5 w-5 text-muted-foreground" />,
    };
  }
  return {
    label: "Gotowe. Wybierz kartę koncepcji.",
    icon: <CheckCircle2 className="h-5 w-5 text-success" />,
  };
}

function ParamsGrid({ compact }: { compact?: boolean }) {
  return (
    <dl
      className={cn(
        "grid gap-x-3 gap-y-2 rounded-md border p-3 text-xs",
        OP_SURFACE.params.box,
        compact ? "grid-cols-2 sm:grid-cols-5" : "grid-cols-2",
      )}
    >
      {PARAMS.map((row) => (
        <div key={row.label}>
          <dt className="text-blue-800/80">{row.label}</dt>
          <dd className="font-medium text-blue-950">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function LogList({ state }: { state: OpAiJobDemoState }) {
  const rows = state === "empty" ? [] : state === "running" ? LOG_RUNNING : LOG_DONE;
  if (!rows.length) {
    return <p className="text-xs text-zinc-400">Log pojawi się po starcie workera.</p>;
  }
  return (
    <ol
      className={cn(
        "max-h-48 space-y-1.5 overflow-auto rounded-md border border-zinc-600 p-2 text-[11px] text-zinc-100",
        OP_SURFACE.log.box,
      )}
    >
      {rows.map((row, i) => (
        <li
          key={`${row.t}-${row.kind}-${i}`}
          className="rounded-md border border-zinc-600 bg-zinc-950/70 px-2 py-1.5 font-mono text-zinc-200"
        >
          <span className="text-zinc-400">{row.t}</span>{" "}
          <span className="font-semibold text-zinc-50">{row.kind}</span>
          {row.text ? <span className="text-zinc-300"> · {row.text}</span> : null}
        </li>
      ))}
    </ol>
  );
}

function Fold({
  title,
  hint,
  defaultOpen,
  children,
  tone = "optional",
}: {
  title: string;
  hint?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  tone?: "optional" | "params" | "log" | "danger";
}) {
  const surface = tone === "danger" ? OP_SURFACE.danger : OP_SURFACE[tone];
  const badge =
    tone === "params" ? "Parametry" : tone === "log" ? "Log" : tone === "danger" ? "Błąd" : "Kontekst";
  const roleTone = tone === "danger" ? ("danger" as const) : tone;
  const darkInk = tone === "log";
  return (
    <details className={cn("group open:shadow-sm", surface.box)} open={defaultOpen}>
      <summary className="cursor-pointer list-none px-3 py-2.5 marker:content-none [&::-webkit-details-marker]:hidden">
        <div className="flex items-center gap-2">
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 transition-transform group-open:rotate-180",
              darkInk ? "text-zinc-400" : "text-slate-500",
            )}
          />
          <OpRoleBadge tone={roleTone} label={badge} />
          <span className={cn("text-sm font-semibold", darkInk ? "text-zinc-50" : "text-slate-800")}>{title}</span>
          {hint ? (
            <span className={cn("ml-auto hidden text-xs sm:inline", darkInk ? "text-zinc-400" : "text-muted-foreground")}>
              {hint}
            </span>
          ) : null}
        </div>
      </summary>
      <div className={cn("space-y-3 px-3 py-3", darkInk ? "border-t border-white/10" : "border-t border-black/5")}>
        {children}
      </div>
    </details>
  );
}


const CONCEPT_STAGES = [
  { id: "memory", label: "Pamięć", bar: "bg-slate-500", track: "bg-slate-200" },
  { id: "prompt", label: "Prompt", bar: "bg-violet-500", track: "bg-violet-200" },
  { id: "generate", label: "Generacja", bar: "bg-brand", track: "bg-brand/20" },
  { id: "cards", label: "Karty", bar: "bg-success", track: "bg-success/20" },
] as const;

const COPY_STAGES = [
  { id: "discussion", label: "Role", bar: "bg-violet-500", track: "bg-violet-200" },
  { id: "copy", label: "Slajdy", bar: "bg-sky-500", track: "bg-sky-200" },
  { id: "review", label: "Recenzja", bar: "bg-orange-500", track: "bg-orange-200" },
] as const;

type DemoJobKind = "concepts" | "copy";

function stagesFor(kind: DemoJobKind) {
  return kind === "copy" ? COPY_STAGES : CONCEPT_STAGES;
}

/** Indeks aktywnego etapu + fraction 0..1 w aktywnym (demo). */
function stageProgress(state: OpAiJobDemoState, kind: DemoJobKind): { activeIdx: number; fraction: number; pct: number } {
  const n = stagesFor(kind).length;
  if (state === "empty") return { activeIdx: -1, fraction: 0, pct: 0 };
  if (state === "done") return { activeIdx: n - 1, fraction: 1, pct: 100 };
  if (state === "failed") {
    const idx = Math.min(n - 1, kind === "copy" ? 1 : 2);
    return { activeIdx: idx, fraction: 0.55, pct: Math.round(((idx + 0.55) / n) * 100) };
  }
  // running: mid pipeline
  const idx = kind === "copy" ? 1 : 2;
  return { activeIdx: idx, fraction: 0.42, pct: Math.round(((idx + 0.42) / n) * 100) };
}

function StageProgressBar({
  state,
  kind,
  loud = false,
}: {
  state: OpAiJobDemoState;
  kind: DemoJobKind;
  loud?: boolean;
}) {
  const stages = stagesFor(kind);
  const { activeIdx, fraction, pct } = stageProgress(state, kind);
  const done = state === "done";
  const failed = state === "failed";

  return (
    <div className="space-y-1.5" aria-label={`Postęp etapów ${pct}%`}>
      <div className={cn("flex gap-1", loud ? "h-3.5" : "h-3")}>
        {stages.map((seg, i) => {
          let fill = 0;
          if (done || i < activeIdx) fill = 1;
          else if (i === activeIdx) fill = failed ? fraction : fraction;
          const barClass = failed && i === activeIdx ? "bg-destructive" : done ? "bg-success" : seg.bar;
          return (
            <div
              key={seg.id}
              className={cn("relative h-full min-w-0 flex-1 overflow-hidden rounded-sm", seg.track)}
              title={seg.label}
            >
              <div
                className={cn(
                  "absolute inset-y-0 left-0 transition-all",
                  barClass,
                  !done && i === activeIdx && !failed && "animate-pulse",
                )}
                style={{ width: `${Math.round(fill * 100)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 text-[10px] text-muted-foreground">
        {stages.map((seg, i) => (
          <span
            key={`l-${seg.id}`}
            className={cn(
              "min-w-0 flex-1 truncate",
              i === activeIdx && state === "running" && "font-semibold text-foreground",
              i === activeIdx && failed && "font-semibold text-destructive",
              done && "font-medium text-success",
              i < activeIdx && !done && "text-foreground/70",
            )}
          >
            {seg.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ProcessHero({ state, kind }: { state: OpAiJobDemoState; kind: DemoJobKind }) {
  if (state === "empty") {
    return (
      <div className={cn("px-5 py-8 text-center", OP_SURFACE.help.box)}>
        <p className="text-sm font-medium text-slate-700">Job jeszcze nie wystartował</p>
        <p className="mt-1 text-xs text-muted-foreground">Po starcie tu zobaczysz pasek etapów (jak w LlmReceiveProgress).</p>
        <div className="mx-auto mt-4 max-w-lg opacity-40">
          <StageProgressBar state="empty" kind={kind} loud />
        </div>
      </div>
    );
  }

  const { activeIdx, pct } = stageProgress(state, kind);
  const stages = stagesFor(kind);
  const currentLabel = activeIdx >= 0 ? stages[activeIdx]?.label : "";
  const done = state === "done";
  const failed = state === "failed";

  if (failed) {
    return (
      <div className={cn("space-y-3 px-5 py-4", OP_SURFACE.danger.box)}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <OpRoleBadge tone="danger" label="Proces · błąd" />
            <p className="mt-1 flex items-center gap-2 text-lg font-semibold text-slate-900">
              <XCircle className="h-5 w-5 text-destructive" />
              Utknięto na etapie: {currentLabel}
            </p>
          </div>
          <span className="text-2xl font-bold tabular-nums text-slate-900">{pct}%</span>
        </div>
        <StageProgressBar state={state} kind={kind} loud />
        <p className="text-xs text-slate-700">
          {activeIdx + 1}/{stages.length} {currentLabel} · timeout / błąd LLM · otwórz log
        </p>
        <Button type="button" variant="destructive" size="sm">
          Odpal ponownie
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "px-5 py-4",
        done ? OP_SURFACE.result.box : OP_SURFACE.process.box,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {done ? (
            <OpRoleBadge tone="result" label="Wynik · odebrany" />
          ) : (
            <OpRoleBadge tone="process" label="Proces · w toku" />
          )}
          <p className="mt-1 flex items-center gap-2 text-lg font-semibold text-slate-900">
            {done ? <CheckCircle2 className="h-5 w-5 text-success" /> : null}
            {!done ? <Loader2 className="h-5 w-5 animate-spin text-cyan-700" /> : null}
            {done
              ? "Odebrano odpowiedź"
              : `${activeIdx + 1}/${stages.length} ${currentLabel} · odbiór treści`}
          </p>
        </div>
        <span className="text-2xl font-bold tabular-nums text-slate-900">{pct}%</span>
      </div>
      <div className="mt-3">
        <StageProgressBar state={state} kind={kind} loud />
      </div>
      <p className="mt-2 text-xs text-slate-700">
        {done
          ? "61 s · 7.4k znaków · 119 zn/s · gemini-3.5-flash · wszystkie etapy"
          : "28 s · 2.1k / ~3.9k · ~95 zn/s · gemini-3.5-flash · leci tekst"}
      </p>
      {!done ? (
        <p className="mt-2 text-sm font-medium text-cyan-900">
          Nie zamykaj. Nie klikaj drugi raz tego samego CTA.
        </p>
      ) : null}
    </div>
  );
}

function OutputHero({
  state,
  selected,
  onSelect,
}: {
  state: OpAiJobDemoState;
  selected: number;
  onSelect: (i: number) => void;
}) {
  if (state === "running") {
    return (
      <div className={cn("px-5 py-10 text-center", OP_SURFACE.process.box)}>
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand" />
        <p className="mt-3 text-sm font-medium text-slate-700">Karty pojawią się po zakończeniu</p>
        <p className="mt-1 text-xs text-muted-foreground">Wyjście jest puste, dopóki proces żyje.</p>
      </div>
    );
  }
  if (state === "empty") {
    return (
      <div className={cn("px-5 py-10 text-center", OP_SURFACE.help.box)}>
        <p className="text-sm font-medium text-slate-700">Brak wyjścia</p>
        <p className="mt-1 text-xs text-muted-foreground">Odpal job, żeby zobaczyć 4 koncepcje.</p>
      </div>
    );
  }
  if (state === "failed") {
    return (
      <OpCallout variant="destructive" title="Brak kart">
        Generacja nie zwróciła 4 koncepcji. Napraw proces, nie wybieraj karty.
      </OpCallout>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <OpRoleBadge tone="result" label="Wynik · wybierz jedną" />
          <h3 className="mt-1 text-lg font-semibold text-indigo-950">4 koncepcje</h3>
        </div>
        <Badge variant="outline">4 / 4</Badge>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {CARDS.map((card, i) => {
          const active = selected === i;
          return (
            <button
              key={card.tag}
              type="button"
              onClick={() => onSelect(i)}
              className={cn("p-4 transition-shadow", active ? OP_RESULT_CARD.selected : OP_RESULT_CARD.idle)}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5">
                  {!active ? <LayoutGrid className="h-3.5 w-3.5 text-indigo-700" aria-hidden /> : null}
                  <Badge variant={active ? "success" : "outline"} className={!active ? "border-indigo-600 text-indigo-900" : undefined}>
                    {card.tag}
                  </Badge>
                </span>
                {active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-success">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    Wybrane
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Wynik</span>
                )}
              </div>
              <p className="text-sm font-medium leading-snug text-slate-900">{card.text}</p>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="button" variant="brand" size="lg">
          <Sparkles className="mr-2 h-4 w-4" />
          Użyj wybranej koncepcji
        </Button>
        <Button type="button" variant="outline" size="lg">
          Generuj ponownie
        </Button>
      </div>
    </div>
  );
}

/** Stary layout: 4 równe kolumny (wszystko tak samo ważne). */
function EqualColumnsLayout({ state, kind }: { state: OpAiJobDemoState; kind: DemoJobKind }) {
  const meta = statusMeta(state);
  const running = state === "running";
  const { pct } = stageProgress(state, kind);

  return (
    <>
      <StudioModalHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-medium">
              {kind === "copy" ? "Generator treści" : "4 koncepcje"}
            </h2>
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              {meta.icon}
              {meta.label}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Weźmie brief, źródła i notatkę. Zwróci wynik joba.
            </p>
          </div>
          <Button size="sm" variant="outline" type="button" disabled={running}>
            Zamknij
          </Button>
        </div>
      </StudioModalHeader>
      <StudioModalBody className="p-0">
        <div className="grid min-h-[420px] lg:grid-cols-4">
          <section className={cn("border-b p-4 lg:border-b-0 lg:border-r", OP_SURFACE.params.box)}>
            <OpRoleBadge tone="params" label="Wejście" className="mb-3" />
            <ParamsGrid />
          </section>
          <section className={cn("border-b p-4 lg:border-b-0 lg:border-r", OP_SURFACE.process.box)}>
            <OpRoleBadge tone="process" label="Przetwarzanie" className="mb-3" />
            {state === "empty" ? (
              <p className="text-xs text-muted-foreground">Jeszcze nic.</p>
            ) : (
              <div className="rounded-md border border-cyan-300 bg-white/50 p-3 text-xs">
                <p className="mb-2 font-medium">Etapy · {pct}%</p>
                <StageProgressBar state={state} kind={kind} />
              </div>
            )}
          </section>
          <section className={cn("border-b p-4 lg:border-b-0 lg:border-r", OP_SURFACE.result.box)}>
            <OpRoleBadge tone="result" label="Wyjście" className="mb-3" />
            {state === "done" ? (
              <div className="space-y-2">
                {CARDS.map((c) => (
                  <div key={c.tag} className={cn("p-2 text-xs", OP_RESULT_CARD.static)}>
                    <span className="inline-flex items-center gap-1 font-semibold text-indigo-950">
                      <LayoutGrid className="h-3 w-3 text-indigo-700" aria-hidden />
                      {c.tag}
                    </span>
                    <p className="mt-1 text-indigo-950">{c.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Brak kart.</p>
            )}
          </section>
          <section className={cn("p-4", OP_SURFACE.log.box)}>
            <OpRoleBadge tone="log" className="mb-3" />
            <LogList state={state} />
          </section>
        </div>
      </StudioModalBody>
      <StudioModalFooter>
        <p className="text-xs text-muted-foreground">Was: 4 równe kolumny. Wszystko konkuruje o uwagę.</p>
      </StudioModalFooter>
    </>
  );
}

/** Nowy layout: jedna dominanta zależnie od stanu. */
function HierarchyLayout({
  state,
  kind,
  onClose,
}: {
  state: OpAiJobDemoState;
  kind: DemoJobKind;
  onClose?: () => void;
}) {
  const [selected, setSelected] = useState(0);
  const [copied, setCopied] = useState(false);
  const meta = statusMeta(state);
  const running = state === "running";

  return (
    <>
      <StudioModalHeader className="bg-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">
                {kind === "copy" ? "Generator treści" : "4 koncepcje"}
              </h2>
              <Badge
                variant={
                  state === "done"
                    ? "success"
                    : state === "running"
                      ? "brand"
                      : state === "failed"
                        ? "destructive"
                        : "secondary"
                }
              >
                {state === "done" ? "gotowe" : state === "running" ? "w toku" : state === "failed" ? "błąd" : "puste"}
              </Badge>
            </div>
            <p className="mt-2 flex items-center gap-2 text-base font-medium text-slate-900">
              {meta.icon}
              {kind === "copy" && state === "done"
                ? "Gotowe. Recenzja i slajdy poniżej."
                : meta.label}
            </p>
          </div>
          <div className="flex gap-2">
            {running ? (
              <Button size="sm" variant="outline" type="button">
                Przerwij
              </Button>
            ) : null}
            <Button size="sm" variant="outline" type="button" disabled={running} onClick={onClose}>
              Zamknij
            </Button>
          </div>
        </div>
      </StudioModalHeader>

      <StudioModalBody className="space-y-4 bg-slate-50/80 p-5">
        {/* Zawsze widoczny pasek etapów (jak LlmReceiveProgress) */}
        <ProcessHero state={state} kind={kind} />

        <div
          className={cn(
            "p-4",
            state === "done" ? OP_SURFACE.result.box : OP_SURFACE.work.box,
          )}
        >
          <OutputHero state={state} selected={selected} onSelect={setSelected} />
        </div>

        <div className="space-y-2">
          <Fold title="Wejście strzału" hint="parametry · nie konkuruje z wynikiem" defaultOpen={false} tone="params">
            <ParamsGrid compact />
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-slate-700">Zapytanie do modelu · 7.9k zn</summary>
              <pre className="mt-2 max-h-24 overflow-auto rounded-md bg-slate-50 p-2 text-[10px] text-slate-600">
                {`SYSTEM: …\nUSER: brief + ból Ciało/wygląd + styl Krem Kasi\n…`}
              </pre>
            </details>
          </Fold>

          <Fold
            title="Log i debug"
            hint="dla awarii / audytu"
            defaultOpen={state === "failed"}
            tone={state === "failed" ? "danger" : "log"}
          >
            <LogList state={state} />
            <dl
              className={cn(
                "mt-3 grid grid-cols-2 gap-2 border-t pt-3 text-[11px] sm:grid-cols-4",
                state === "failed" ? "border-black/10" : "border-white/10 text-zinc-100",
              )}
            >
              <div>
                <dt className={state === "failed" ? "text-muted-foreground" : "text-zinc-400"}>Job</dt>
                <dd className={cn("font-medium", state !== "failed" && "text-zinc-50")}>
                  {kind === "copy" ? "copy" : "concepts"}
                </dd>
              </div>
              <div>
                <dt className={state === "failed" ? "text-muted-foreground" : "text-zinc-400"}>Faza</dt>
                <dd className={cn("font-medium", state !== "failed" && "text-zinc-50")}>
                  {state === "done" ? "done" : state === "running" ? "streaming" : "n/d"}
                </dd>
              </div>
              <div>
                <dt className={state === "failed" ? "text-muted-foreground" : "text-zinc-400"}>Model</dt>
                <dd className={cn("font-medium", state !== "failed" && "text-zinc-50")}>gemini-3.5-flash</dd>
              </div>
              <div>
                <dt className={state === "failed" ? "text-muted-foreground" : "text-zinc-400"}>Celery</dt>
                <dd
                  className={cn(
                    "truncate font-mono text-[10px]",
                    state !== "failed" && "text-zinc-200",
                  )}
                >
                  7d37b107…
                </dd>
              </div>
            </dl>
            <Button type="button" size="sm" variant="outline" className="mt-2" disabled={state === "empty"}>
              <Copy className="mr-1.5 h-3 w-3" />
              Kopiuj log
            </Button>
          </Fold>
        </div>
      </StudioModalBody>

      <StudioModalFooter className="flex flex-wrap items-center justify-between gap-3 bg-white">
        <p className="text-xs text-muted-foreground">
          Pasek etapów zawsze nad wynikiem (ten sam wzorzec co LlmReceiveProgress / BgCandidates).
        </p>
        <Button
          size="sm"
          variant="outline"
          type="button"
          disabled={running || state === "empty"}
          onClick={() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          }}
        >
          <Copy className="mr-2 h-3.5 w-3.5" />
          {copied ? "Skopiowano" : "Kopiuj przebieg"}
        </Button>
      </StudioModalFooter>
    </>
  );
}

export function OpAiJobModalPanel({
  state,
  layout = "hierarchy",
  kind = "concepts",
  onClose,
  className,
}: {
  state: OpAiJobDemoState;
  layout?: LayoutMode;
  kind?: DemoJobKind;
  onClose?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex h-[min(92vh,920px)] w-full max-w-[1440px] flex-col overflow-hidden rounded-lg border-2 border-slate-300 bg-card text-card-foreground shadow-xl",
        className,
      )}
    >
      {layout === "equal" ? (
        <EqualColumnsLayout state={state} kind={kind} />
      ) : (
        <HierarchyLayout state={state} kind={kind} onClose={onClose} />
      )}
    </div>
  );
}

export function OpAiJobModalOverlay({
  open,
  state,
  layout,
  kind,
  onClose,
}: {
  open: boolean;
  state: OpAiJobDemoState;
  layout: LayoutMode;
  kind: DemoJobKind;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-3"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="op-ai-job-demo-title"
        className="w-[min(96vw,1100px)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="op-ai-job-demo-title" className="sr-only">
          Demo modalu AI
        </h2>
        <OpAiJobModalPanel state={state} layout={layout} kind={kind} onClose={onClose} />
      </div>
    </div>
  );
}

export function OpAiJobModalDemoTab() {
  const [state, setState] = useState<OpAiJobDemoState>("running");
  const [layout, setLayout] = useState<LayoutMode>("hierarchy");
  const [kind, setKind] = useState<DemoJobKind>("copy");
  const [overlay, setOverlay] = useState(false);

  return (
    <div className="space-y-4">
      <OpCallout variant="help" title="Pasek etapów wrócił">
        Ten sam wzorzec co w modalu produkcji (`LlmReceiveProgress`): segmenty + etykiety pod spodem.
        Job <strong>copy</strong> = Role / Slajdy / Recenzja. Job <strong>concepts</strong> = Pamięć / Prompt / Generacja / Karty.
      </OpCallout>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={layout === "hierarchy" ? "brand" : "outline"}
          onClick={() => setLayout("hierarchy")}
        >
          Jest · hierarchia
        </Button>
        <Button
          type="button"
          size="sm"
          variant={layout === "equal" ? "brand" : "outline"}
          onClick={() => setLayout("equal")}
        >
          Was · 4 kolumny
        </Button>
        <span className="mx-1 hidden h-5 w-px bg-border sm:inline-block" />
        <Button
          type="button"
          size="sm"
          variant={kind === "copy" ? "brand" : "outline"}
          onClick={() => setKind("copy")}
        >
          Job: copy (3 etapy)
        </Button>
        <Button
          type="button"
          size="sm"
          variant={kind === "concepts" ? "brand" : "outline"}
          onClick={() => setKind("concepts")}
        >
          Job: concepts (4 etapy)
        </Button>
        <span className="mx-1 hidden h-5 w-px bg-border sm:inline-block" />
        {(
          [
            ["done", "Gotowe"],
            ["running", "W toku"],
            ["failed", "Błąd"],
            ["empty", "Puste"],
          ] as const
        ).map(([id, label]) => (
          <Button
            key={id}
            type="button"
            size="sm"
            variant={state === id ? "brand" : "outline"}
            onClick={() => setState(id)}
          >
            {label}
          </Button>
        ))}
        <Button type="button" size="sm" variant="brand" className="ml-auto" onClick={() => setOverlay(true)}>
          Otwórz jako modal
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>

      <div className="overflow-auto rounded-xl border-2 border-dashed border-slate-300 bg-slate-100/80 p-3">
        <OpAiJobModalPanel state={state} layout={layout} kind={kind} onClose={() => undefined} />
      </div>

      <OpAiJobModalOverlay
        open={overlay}
        state={state}
        layout={layout}
        kind={kind}
        onClose={() => setOverlay(false)}
      />
    </div>
  );
}
